// Ecliptica Tarot — SVG Art Generator
// Returns SVG inner content (no outer <svg> tag) for each card face

import { TAROT_DATA } from "./tarotArtData";

const TEAL   = '#3ecfca';
const LTEAL  = '#7dd8e8';
const BG     = '#071418';
const BGCARD = '#0b1e24';
const DIM    = 'rgba(62,207,202,0.18)';
const MID    = 'rgba(62,207,202,0.35)';
const BRIGHT = 'rgba(62,207,202,0.7)';

function circle(cx,cy,r,opts='') { return `<circle cx="${cx}" cy="${cy}" r="${r}" ${opts}/>`; }
function line(x1,y1,x2,y2,opts='') { return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" ${opts}/>`; }
function polygon(pts,opts='') { return `<polygon points="${pts}" ${opts}/>`; }
function path(d,opts='') { return `<path d="${d}" ${opts}/>`; }
function text(x,y,s,opts='') { return `<text x="${x}" y="${y}" ${opts}>${s}</text>`; }
function rect(x,y,w,h,opts='') { return `<rect x="${x}" y="${y}" width="${w}" height="${h}" ${opts}/>`; }

// Radial rays helper
function rays(cx,cy,r1,r2,n,color='#3ecfca',sw=0.7,op=0.5) {
  return Array.from({length:n}).map((_,i) => {
    const a = (i/n)*Math.PI*2;
    return `<line x1="${(cx+Math.cos(a)*r1).toFixed(1)}" y1="${(cy+Math.sin(a)*r1).toFixed(1)}" x2="${(cx+Math.cos(a)*r2).toFixed(1)}" y2="${(cy+Math.sin(a)*r2).toFixed(1)}" stroke="${color}" stroke-width="${sw}" opacity="${op}"/>`;
  }).join('');
}

// Star polygon
function star(cx,cy,r1,r2,pts,opts='') {
  const points = Array.from({length:pts*2}).map((_,i) => {
    const a = (i/pts/2)*Math.PI*2 - Math.PI/2;
    const r = i%2===0 ? r1 : r2;
    return `${(cx+Math.cos(a)*r).toFixed(1)},${(cy+Math.sin(a)*r).toFixed(1)}`;
  }).join(' ');
  return `<polygon points="${points}" ${opts}/>`;
}

// Concentric circles
function rings(cx,cy,radii,stroke=TEAL,op=0.3,sw=0.6) {
  return radii.map(r => `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${stroke}" stroke-width="${sw}" opacity="${op}"/>`).join('');
}

// ── MAJOR ARCANA art functions ──
const MAJOR_ART = {

  fool() {
    return `
      ${rings(100,130,[55,42,30,18],'#3ecfca',0.2)}
      ${rays(100,130,24,52,16,'#3ecfca',0.5,0.3)}
      ${star(100,130,22,10,8,'fill="none" stroke="#3ecfca" stroke-width="1" opacity="0.7"')}
      ${circle(100,130,6,'fill="#3ecfca" opacity="0.5"')}
      ${circle(100,75,10,'fill="none" stroke="#7dd8e8" stroke-width="0.8" opacity="0.6"')}
      <line x1="100" y1="65" x2="100" y2="108" stroke="#3ecfca" stroke-width="0.7" opacity="0.4"/>
      ${circle(100,75,4,'fill="#7dd8e8" opacity="0.7"')}
      <path d="M70,200 Q100,160 130,200" fill="none" stroke="#3ecfca" stroke-width="1" opacity="0.3"/>`;
  },

  magician() {
    return `
      ${circle(100,95,38,'fill="none" stroke="#3ecfca" stroke-width="1" opacity="0.5"')}
      ${rays(100,95,40,56,12,'#3ecfca',0.6,0.4)}
      <rect x="74" y="69" width="52" height="52" rx="2" fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.35" transform="rotate(45 100 95)"/>
      ${circle(100,95,16,'fill="rgba(62,207,202,0.12)"')}
      ${rays(100,95,0,14,4,'#3ecfca',1.2,0.8)}
      <line x1="100" y1="55" x2="100" y2="68" stroke="#7dd8e8" stroke-width="1" opacity="0.6"/>
      ${circle(100,52,5,'fill="#7dd8e8" opacity="0.8"')}
      <path d="M72,140 L128,140 M86,130 L86,150 M100,128 L100,152 M114,130 L114,150" stroke="#3ecfca" stroke-width="0.8" opacity="0.4"/>`;
  },

  priestess() {
    return `
      ${circle(100,90,46,'fill="none" stroke="#7dd8e8" stroke-width="0.7" opacity="0.3"')}
      ${circle(100,90,32,'fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.4"')}
      <path d="M100,44 L100,136" stroke="#3ecfca" stroke-width="0.7" opacity="0.4"/>
      <path d="M54,90 L146,90" stroke="#3ecfca" stroke-width="0.7" opacity="0.4"/>
      ${circle(100,90,10,'fill="rgba(125,216,232,0.2)"')}
      <path d="M78,58 Q100,44 122,58 Q140,78 122,98 Q100,120 78,98 Q60,78 78,58Z" fill="none" stroke="#7dd8e8" stroke-width="0.8" opacity="0.5"/>
      <path d="M88,72 Q76,90 88,108 Q100,94 100,90 Q100,86 88,72Z" fill="rgba(125,216,232,0.18)"/>
      <line x1="70" y1="150" x2="130" y2="150" stroke="#3ecfca" stroke-width="0.6" opacity="0.3"/>
      <line x1="80" y1="158" x2="120" y2="158" stroke="#3ecfca" stroke-width="0.4" opacity="0.2"/>`;
  },

  empress() {
    return `
      ${circle(100,100,44,'fill="none" stroke="#7dd8e8" stroke-width="1" opacity="0.4"')}
      ${star(100,100,36,18,6,'fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.5"')}
      ${rays(100,100,46,60,12,'#3ecfca',0.5,0.25)}
      ${circle(100,100,12,'fill="rgba(62,207,202,0.15)"')}
      <path d="M76,76 Q100,56 124,76 Q140,100 124,124 Q100,144 76,124 Q60,100 76,76Z" fill="none" stroke="#3ecfca" stroke-width="0.7" opacity="0.3"/>
      ${circle(100,100,5,'fill="#3ecfca" opacity="0.6"')}`;
  },

  emperor() {
    return `
      <rect x="60" y="55" width="80" height="90" rx="2" fill="none" stroke="#3ecfca" stroke-width="1" opacity="0.4"/>
      <rect x="72" y="67" width="56" height="66" rx="1" fill="none" stroke="#7dd8e8" stroke-width="0.7" opacity="0.3"/>
      ${rays(100,100,0,20,4,'#3ecfca',1.2,0.6)}
      ${circle(100,100,10,'fill="rgba(62,207,202,0.12)"')}
      <line x1="100" y1="55" x2="100" y2="72" stroke="#3ecfca" stroke-width="1" opacity="0.5"/>
      ${circle(100,52,6,'fill="none" stroke="#3ecfca" stroke-width="1" opacity="0.7"')}
      ${circle(100,52,2,'fill="#3ecfca" opacity="0.8"')}`;
  },

  hierophant() {
    return `
      ${rays(100,95,0,50,5,'#3ecfca',0.8,0.35)}
      ${rings(100,95,[50,38,26],'#3ecfca',0.25)}
      <rect x="84" y="55" width="32" height="80" rx="16" fill="none" stroke="#7dd8e8" stroke-width="0.8" opacity="0.4"/>
      ${circle(100,95,10,'fill="rgba(62,207,202,0.18)"')}
      <line x1="84" y1="145" x2="116" y2="145" stroke="#3ecfca" stroke-width="0.7" opacity="0.4"/>
      <line x1="90" y1="155" x2="110" y2="155" stroke="#3ecfca" stroke-width="0.5" opacity="0.3"/>`;
  },

  lovers() {
    return `
      ${circle(76,105,28,'fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.4"')}
      ${circle(124,105,28,'fill="none" stroke="#7dd8e8" stroke-width="0.8" opacity="0.4"')}
      ${circle(100,72,22,'fill="none" stroke="#3ecfca" stroke-width="1" opacity="0.5"')}
      <line x1="76" y1="105" x2="124" y2="105" stroke="#3ecfca" stroke-width="0.6" opacity="0.3"/>
      ${rays(100,72,24,38,8,'#3ecfca',0.5,0.3)}
      ${circle(100,72,8,'fill="rgba(62,207,202,0.2)"')}
      ${circle(76,105,6,'fill="#3ecfca" opacity="0.4"')}
      ${circle(124,105,6,'fill="#7dd8e8" opacity="0.4"')}`;
  },

  chariot() {
    return `
      <rect x="62" y="62" width="76" height="76" rx="2" fill="none" stroke="#3ecfca" stroke-width="1" opacity="0.4"/>
      ${rays(100,100,0,28,8,'#3ecfca',0.8,0.4)}
      <line x1="68" y1="150" x2="90" y2="138" stroke="#7dd8e8" stroke-width="0.7" opacity="0.4"/>
      <line x1="132" y1="150" x2="110" y2="138" stroke="#7dd8e8" stroke-width="0.7" opacity="0.4"/>
      ${circle(84,150,6,'fill="rgba(62,207,202,0.2)" stroke="#3ecfca" stroke-width="0.7"')}
      ${circle(116,150,6,'fill="rgba(125,216,232,0.2)" stroke="#7dd8e8" stroke-width="0.7"')}
      ${circle(100,100,12,'fill="rgba(62,207,202,0.15)"')}
      ${star(100,100,10,5,6,'fill="#3ecfca" opacity="0.5"')}`;
  },

  strength() {
    return `
      ${circle(100,95,44,'fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.35"')}
      <path d="M66,95 Q100,55 134,95 Q100,135 66,95Z" fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.5"/>
      ${rays(100,95,46,60,8,'#3ecfca',0.5,0.3)}
      <path d="M82,78 Q100,68 118,78" fill="none" stroke="#7dd8e8" stroke-width="1.2" opacity="0.6"/>
      ${circle(100,95,14,'fill="rgba(62,207,202,0.1)"')}
      ${star(100,95,12,6,5,'fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.6"')}`;
  },

  hermit() {
    return `
      ${rays(100,80,82,100,1,'#3ecfca',1.5,0.7)}
      ${rays(100,80,82,100,1,'#7dd8e8',1.2,0.4)}
      ${circle(100,80,12,'fill="rgba(62,207,202,0.15)" stroke="#3ecfca" stroke-width="0.8" opacity="0.6"')}
      <path d="M100,92 L100,160" stroke="#3ecfca" stroke-width="1" opacity="0.4"/>
      ${rings(100,80,[30,50,70],'#3ecfca',0.15)}
      <line x1="100" y1="68" x2="130" y2="50" stroke="#7dd8e8" stroke-width="0.8" opacity="0.5"/>
      ${circle(130,50,4,'fill="#7dd8e8" opacity="0.7"')}`;
  },

  wheel() {
    return `
      ${rings(100,95,[56,44,32,20,8],'#3ecfca',0.3,0.6)}
      ${rays(100,95,22,54,8,'#3ecfca',0.7,0.4)}
      ${rays(100,95,8,20,4,'#7dd8e8',1,0.5)}
      ${circle(100,95,8,'fill="rgba(62,207,202,0.25)"')}
      ${star(100,95,58,54,8,'fill="none" stroke="#3ecfca" stroke-width="0.5" opacity="0.3"')}`;
  },

  justice() {
    return `
      <line x1="100" y1="48" x2="100" y2="162" stroke="#3ecfca" stroke-width="1" opacity="0.5"/>
      <line x1="60" y1="80" x2="140" y2="80" stroke="#3ecfca" stroke-width="0.8" opacity="0.4"/>
      ${circle(70,105,22,'fill="none" stroke="#3ecfca" stroke-width="0.7" opacity="0.35"')}
      ${circle(130,105,22,'fill="none" stroke="#7dd8e8" stroke-width="0.7" opacity="0.35"')}
      <line x1="70" y1="80" x2="70" y2="105" stroke="#3ecfca" stroke-width="0.6" opacity="0.4"/>
      <line x1="130" y1="80" x2="130" y2="105" stroke="#7dd8e8" stroke-width="0.6" opacity="0.4"/>
      ${circle(100,80,4,'fill="#3ecfca" opacity="0.7"')}
      <path d="M90,155 L100,148 L110,155" fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.5"/>`;
  },

  hanged() {
    return `
      <line x1="68" y1="55" x2="132" y2="55" stroke="#3ecfca" stroke-width="1" opacity="0.5"/>
      <line x1="100" y1="55" x2="100" y2="80" stroke="#3ecfca" stroke-width="0.8" opacity="0.5"/>
      ${circle(100,100,22,'fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.4"')}
      ${rays(100,100,24,38,8,'#7dd8e8',0.5,0.3)}
      ${circle(100,100,10,'fill="rgba(62,207,202,0.18)"')}
      <line x1="100" y1="122" x2="80" y2="148" stroke="#3ecfca" stroke-width="0.7" opacity="0.35"/>
      <line x1="100" y1="122" x2="120" y2="148" stroke="#3ecfca" stroke-width="0.7" opacity="0.35"/>`;
  },

  death() {
    return `
      ${circle(100,95,50,'fill="none" stroke="#3ecfca" stroke-width="0.5" opacity="0.2"')}
      ${rays(100,95,0,48,13,'#3ecfca',0.6,0.25)}
      <path d="M100,45 L120,95 L100,145 L80,95 Z" fill="rgba(62,207,202,0.08)" stroke="#3ecfca" stroke-width="0.8" opacity="0.5"/>
      ${circle(100,95,10,'fill="rgba(62,207,202,0.15)"')}
      <path d="M70,140 Q100,120 130,140" fill="none" stroke="#7dd8e8" stroke-width="0.8" opacity="0.4"/>`;
  },

  temperance() {
    return `
      ${circle(78,90,30,'fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.4"')}
      ${circle(122,110,30,'fill="none" stroke="#7dd8e8" stroke-width="0.8" opacity="0.4"')}
      <path d="M78,60 Q100,90 122,80" fill="none" stroke="#3ecfca" stroke-width="1" opacity="0.5"/>
      <path d="M78,120 Q100,100 122,140" fill="none" stroke="#7dd8e8" stroke-width="0.8" opacity="0.4"/>
      ${circle(100,100,8,'fill="rgba(62,207,202,0.25)"')}
      ${rays(100,100,10,22,8,'#3ecfca',0.5,0.4)}`;
  },

  devil() {
    return `
      ${circle(100,88,48,'fill="none" stroke="#3ecfca" stroke-width="1" opacity="0.3"')}
      <path d="M72,62 L100,50 L128,62" fill="none" stroke="#3ecfca" stroke-width="1" opacity="0.5"/>
      <path d="M72,62 L60,88 L100,130 L140,88 L128,62" fill="none" stroke="#3ecfca" stroke-width="0.7" opacity="0.35"/>
      ${circle(100,88,14,'fill="rgba(62,207,202,0.1)"')}
      ${star(100,88,12,6,5,'fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.5"')}
      ${circle(78,120,6,'fill="rgba(62,207,202,0.2)" stroke="#3ecfca" stroke-width="0.6"')}
      ${circle(122,120,6,'fill="rgba(62,207,202,0.2)" stroke="#3ecfca" stroke-width="0.6"')}`;
  },

  tower() {
    return `
      <rect x="82" y="50" width="36" height="110" rx="2" fill="none" stroke="#3ecfca" stroke-width="1" opacity="0.45"/>
      ${circle(100,48,10,'fill="rgba(62,207,202,0.15)" stroke="#3ecfca" stroke-width="0.8" opacity="0.6"')}
      ${rays(100,48,12,26,8,'#3ecfca',0.6,0.4)}
      <path d="M82,90 L62,120" stroke="#7dd8e8" stroke-width="1" opacity="0.5"/>
      <path d="M118,85 L142,110" stroke="#7dd8e8" stroke-width="1" opacity="0.5"/>
      ${circle(65,123,4,'fill="#7dd8e8" opacity="0.5"')}
      ${circle(144,112,3,'fill="#7dd8e8" opacity="0.4"')}
      <line x1="90" y1="80" x2="110" y2="80" stroke="#3ecfca" stroke-width="0.6" opacity="0.35"/>`;
  },

  star() {
    return `
      ${star(100,92,42,20,8,'fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.4"')}
      ${star(100,92,24,12,8,'fill="rgba(62,207,202,0.1)" stroke="#7dd8e8" stroke-width="0.7" opacity="0.6"')}
      ${rays(100,92,44,60,16,'#3ecfca',0.4,0.2)}
      ${circle(100,92,8,'fill="#3ecfca" opacity="0.5"')}
      ${[0,1,2,3,4,5,6,7].map(i => {
        const a = (i/8)*Math.PI*2;
        const r = 52;
        return `<circle cx="${(100+Math.cos(a)*r).toFixed(1)}" cy="${(92+Math.sin(a)*r).toFixed(1)}" r="3" fill="#3ecfca" opacity="0.3"/>`;
      }).join('')}`;
  },

  moon() {
    return `
      <path d="M70,80 Q60,100 70,120 Q88,110 88,100 Q88,90 70,80Z" fill="rgba(125,216,232,0.2)" stroke="#7dd8e8" stroke-width="0.8" opacity="0.6"/>
      ${circle(110,100,34,'fill="none" stroke="#3ecfca" stroke-width="0.7" opacity="0.35"')}
      ${rings(110,100,[20,28],'#3ecfca',0.2)}
      ${circle(110,100,8,'fill="rgba(62,207,202,0.15)"')}
      <path d="M60,150 Q80,140 100,148 Q120,156 140,145" fill="none" stroke="#3ecfca" stroke-width="0.7" opacity="0.3"/>
      ${rays(110,100,36,50,8,'#3ecfca',0.4,0.2)}`;
  },

  sun() {
    return `
      ${circle(100,90,40,'fill="rgba(62,207,202,0.1)" stroke="#3ecfca" stroke-width="1.2" opacity="0.6"')}
      ${rays(100,90,42,62,16,'#3ecfca',0.8,0.5)}
      ${rays(100,90,64,72,8,'#7dd8e8',0.5,0.3)}
      ${circle(100,90,18,'fill="rgba(62,207,202,0.2)"')}
      ${star(100,90,14,7,6,'fill="#3ecfca" opacity="0.4"')}
      ${circle(100,90,5,'fill="#3ecfca" opacity="0.8"')}`;
  },

  judgement() {
    return `
      ${rays(100,70,0,50,6,'#3ecfca',1,0.5)}
      ${circle(100,70,14,'fill="rgba(62,207,202,0.18)" stroke="#3ecfca" stroke-width="0.8" opacity="0.6"')}
      ${[[-22,115],[0,130],[22,115]].map(([dx,dy],i) =>
        `${circle(100+dx,dy,8,'fill="rgba(62,207,202,0.15)" stroke="#3ecfca" stroke-width="0.7" opacity="0.5"')}`
      ).join('')}
      ${rays(100,70,16,50,8,'#7dd8e8',0.4,0.25)}
      <path d="M78,115 Q100,105 122,115 Q110,130 100,140 Q90,130 78,115Z" fill="rgba(62,207,202,0.08)" stroke="#3ecfca" stroke-width="0.7" opacity="0.4"/>`;
  },

  world() {
    return `
      ${circle(100,95,52,'fill="none" stroke="#3ecfca" stroke-width="1" opacity="0.4"')}
      <path d="M100,43 Q130,68 130,95 Q130,122 100,147 Q70,122 70,95 Q70,68 100,43Z" fill="none" stroke="#7dd8e8" stroke-width="0.7" opacity="0.35"/>
      ${star(100,95,18,9,4,'fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.6"')}
      ${rays(100,95,54,68,8,'#3ecfca',0.5,0.25)}
      ${circle(100,95,8,'fill="rgba(62,207,202,0.25)"')}
      ${[0,1,2,3].map(i => {
        const a = (i/4)*Math.PI*2;
        return `<circle cx="${(100+Math.cos(a)*54).toFixed(1)}" cy="${(95+Math.sin(a)*54).toFixed(1)}" r="4" fill="#3ecfca" opacity="0.4"/>`;
      }).join('')}`
  },
};

// ── MINOR ARCANA art ──
// Returns suit-themed geometric composition
function minorArt(suitIdx, pipIdx) {
  const suit = TAROT_DATA.suits[suitIdx];
  const count = Math.min(pipIdx + 1, 10);
  const isCourt = pipIdx >= 10;
  const tc = suit.tcolor;

  // Court cards get special compositions
  if (isCourt) {
    const courtArts = {
      10: (tc) => `
        ${circle(100,95,42,`fill="none" stroke="${tc}0.5)" stroke-width="1.2"`)}
        ${rays(100,95,44,60,12,`${tc}0.8)`,0.5,0.35)}
        ${star(100,95,20,10,5,`fill="none" stroke="${tc}0.8)" stroke-width="1"`)}
        ${circle(100,95,8,`fill="${tc}0.25)"`)}
        <text x="100" y="185" text-anchor="middle" font-family="'DM Mono',monospace" font-size="11" fill="${tc}0.7)" letter-spacing="3">PAGE</text>`,
      11: (tc) => `
        ${circle(100,85,38,`fill="none" stroke="${tc}0.5)" stroke-width="1"`)}
        <rect x="66" y="51" width="68" height="68" rx="2" fill="none" stroke="${tc}0.35)" stroke-width="0.7" transform="rotate(45 100 85)"/>
        ${rays(100,85,40,55,8,`${tc}0.7)`,0.6,0.4)}
        ${circle(100,85,10,`fill="${tc}0.2)"`)}
        <line x1="100" y1="123" x2="80" y2="155" stroke="${tc}0.4)" stroke-width="0.8"/>
        <line x1="100" y1="123" x2="120" y2="155" stroke="${tc}0.4)" stroke-width="0.8"/>
        <text x="100" y="185" text-anchor="middle" font-family="'DM Mono',monospace" font-size="11" fill="${tc}0.7)" letter-spacing="3">KNIGHT</text>`,
      12: (tc) => `
        ${circle(100,88,46,`fill="none" stroke="${tc}0.5)" stroke-width="1.2"`)}
        ${rays(100,88,48,65,16,`${tc}0.7)`,0.5,0.3)}
        ${star(100,88,28,14,6,`fill="none" stroke="${tc}0.7)" stroke-width="0.8"`)}
        ${circle(100,88,10,`fill="${tc}0.2)"`)}
        <text x="100" y="185" text-anchor="middle" font-family="'DM Mono',monospace" font-size="11" fill="${tc}0.7)" letter-spacing="3">QUEEN</text>`,
      13: (tc) => `
        ${circle(100,85,50,`fill="none" stroke="${tc}0.5)" stroke-width="1.4"`)}
        ${rays(100,85,52,70,16,`${tc}0.8)`,0.7,0.4)}
        <rect x="50" y="35" width="100" height="100" rx="2" fill="none" stroke="${tc}0.25)" stroke-width="0.7"/>
        ${star(100,85,32,16,4,`fill="rgba(62,207,202,0.1)" stroke="${tc}0.7)" stroke-width="0.8"`)}
        ${circle(100,85,12,`fill="${tc}0.25)"`)}
        <text x="100" y="185" text-anchor="middle" font-family="'DM Mono',monospace" font-size="11" fill="${tc}0.7)" letter-spacing="3">KING</text>`,
    };
    return courtArts[pipIdx] ? courtArts[pipIdx](tc) : '';
  }

  // Pip cards — arrange suit symbols in a grid
  const positions = {
    1:  [[100,110]],
    2:  [[100,80],[100,140]],
    3:  [[100,72],[80,120],[120,120]],
    4:  [[78,80],[122,80],[78,130],[122,130]],
    5:  [[78,72],[122,72],[100,100],[78,130],[122,130]],
    6:  [[78,72],[122,72],[78,100],[122,100],[78,130],[122,130]],
    7:  [[78,65],[122,65],[100,87],[78,108],[122,108],[78,130],[122,130]],
    8:  [[78,65],[122,65],[78,90],[122,90],[78,115],[122,115],[78,140],[122,140]],
    9:  [[78,60],[100,60],[122,60],[78,95],[100,95],[122,95],[78,130],[100,130],[122,130]],
    10: [[78,58],[100,58],[122,58],[78,83],[100,83],[122,83],[78,110],[122,110],[78,135],[122,135]],
  };

  const pts = positions[count] || positions[1];

  // Suit-specific symbol
  const symFn = {
    0: (x,y) => `<line x1="${x}" y1="${y-10}" x2="${x}" y2="${y+10}" stroke="${tc}0.8)" stroke-width="1.5"/><line x1="${x-5}" y1="${y}" x2="${x+5}" y2="${y}" stroke="${tc}0.6)" stroke-width="1"/><circle cx="${x}" cy="${y-10}" r="3" fill="${tc}0.5)"/>`, // Wand
    1: (x,y) => `<circle cx="${x}" cy="${y}" r="8" fill="none" stroke="${tc}0.7)" stroke-width="1"/><circle cx="${x}" cy="${y}" r="4" fill="${tc}0.25)"/>`, // Cup
    2: (x,y) => `<polygon points="${x},${y-9} ${x-8},${y+7} ${x+8},${y+7}" fill="none" stroke="${tc}0.7)" stroke-width="1"/>`, // Sword
    3: (x,y) => `${star(x,y,9,5,5,`fill="none" stroke="${tc}0.7)" stroke-width="1"`)}`, // Pentacle
  };

  const sym = symFn[suitIdx] || symFn[1];
  return pts.map(([x,y]) => sym(x,y)).join('\n');
}

// Ace gets special treatment
function aceArt(suitIdx) {
  const suit = TAROT_DATA.suits[suitIdx];
  const tc = suit.tcolor;
  const aceDesigns = [
    () => `${circle(100,95,40,`fill="none" stroke="${tc}0.5)" stroke-width="1.2"`)}${rays(100,95,42,62,12,`${tc}0.8)`,0.7,0.4)}<line x1="100" y1="55" x2="100" y2="135" stroke="${tc}0.8)" stroke-width="1.5"/><circle cx="100" cy="55" r="5" fill="${tc}0.7)"/>${rays(100,95,0,38,4,`${tc}0.8)`,1,0.5)}`,
    () => `${circle(100,90,44,`fill="none" stroke="${tc}0.5)" stroke-width="1.2"`)}${circle(100,90,28,`fill="${tc}0.08)"`)}${circle(100,90,14,`fill="${tc}0.15)"`)}${rays(100,90,0,12,8,`${tc}0.9)`,1,0.6)}${circle(100,90,5,`fill="${tc}0.7)"`)}`,
    () => `${star(100,95,44,22,4,`fill="none" stroke="${tc}0.5)" stroke-width="1.2"`)}${rays(100,95,46,62,8,`${tc}0.7)`,0.6,0.35)}${circle(100,95,14,`fill="${tc}0.12)"`)}${star(100,95,12,6,4,`fill="${tc}0.4)"`)}`,
    () => `${star(100,95,36,18,5,`fill="none" stroke="${tc}0.5)" stroke-width="1.2"`)}${rays(100,95,38,58,10,`${tc}0.7)`,0.5,0.3)}${circle(100,95,12,`fill="${tc}0.15)"`)}${star(100,95,10,5,5,`fill="${tc}0.5)"`)}`,
  ];
  return aceDesigns[suitIdx]();
}

export function getCardArt(card) {
  if (card.type === 'major') {
    const fn = MAJOR_ART[card.art];
    return fn ? fn() : '';
  }
  if (card.pipIdx === 0) return aceArt(card.suitIdx);
  return minorArt(card.suitIdx, card.pipIdx);
}

// ── hex → "r,g,b" helper ──
export function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

// ── Card dimensions ──
export const CARD_W = 160;
export const CARD_H = 272;
export const SUIT_COLORS = ['#e07040','#3ecfca','#7dd8e8','#7ec87a'];

// ── Build card face SVG string ──
// Returns a full <svg>…</svg> string ready to inject as innerHTML
export function buildCardFaceSVG(card, w, h) {
  w = w || CARD_W;
  h = h || CARD_H;
  const isMinor   = card.type === 'minor';
  const suitColor = isMinor ? SUIT_COLORS[card.suitIdx] : '#3ecfca';
  const art       = getCardArt(card);
  const uid       = `${card.num}_${card.suit || 'm'}`;

  return `<svg viewBox="0 0 ${CARD_W} ${CARD_H}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" style="display:block;width:100%;height:100%">
  <defs>
    <radialGradient id="cgbg_${uid}" cx="50%" cy="35%">
      <stop offset="0%" stop-color="#0e2830"/>
      <stop offset="100%" stop-color="#050d10"/>
    </radialGradient>
    <filter id="cglow_${uid}">
      <feGaussianBlur stdDeviation="2.5" result="blur"/>
      <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
    </filter>
  </defs>

  <!-- Background -->
  <rect width="${CARD_W}" height="${CARD_H}" fill="url(#cgbg_${uid})"/>

  <!-- Outer border -->
  <rect x="6" y="6" width="${CARD_W-12}" height="${CARD_H-12}" rx="2"
    fill="none" stroke="${suitColor}" stroke-width="1" opacity="0.45"/>
  <!-- Inner border -->
  <rect x="10" y="10" width="${CARD_W-20}" height="${CARD_H-20}" rx="1"
    fill="none" stroke="${suitColor}" stroke-width="0.5" opacity="0.22"/>

  <!-- Top rule -->
  <line x1="10" y1="30" x2="${CARD_W-10}" y2="30" stroke="${suitColor}" stroke-width="0.5" opacity="0.22"/>
  <!-- Bottom rule -->
  <line x1="10" y1="${CARD_H-44}" x2="${CARD_W-10}" y2="${CARD_H-44}" stroke="${suitColor}" stroke-width="0.5" opacity="0.22"/>

  <!-- Corner number (Arabic) -->
  <text x="16" y="24" font-family="'DM Mono',monospace" font-size="9"
    fill="${suitColor}" opacity="0.84" letter-spacing="1">${card.num}</text>

  <!-- Roman numeral centre-top -->
  <text x="${CARD_W/2}" y="24" text-anchor="middle" font-family="'Playfair Display',serif" font-size="10"
    fill="${suitColor}" opacity="0.84" letter-spacing="1">${card.roman}</text>

  <!-- Art -->
  ${art}

  <!-- Name plate for better revealed-face readability -->
  <rect x="14" y="${CARD_H-42}" width="${CARD_W-28}" height="22" rx="3" fill="rgba(5,13,16,0.62)"/>

  <!-- Card name -->
  <text x="${CARD_W/2}" y="${CARD_H-28}" text-anchor="middle"
    font-family="'Playfair Display',serif" font-size="${card.name.length > 16 ? 9.5 : 11}"
    fill="${suitColor}" opacity="0.98" letter-spacing="0.5">${card.name.toUpperCase()}</text>

  ${isMinor
    ? `<text x="${CARD_W-16}" y="24" text-anchor="end" font-family="'DM Mono',monospace" font-size="8"
        fill="${suitColor}" opacity="0.5">${card.suit.charAt(0)}</text>`
    : `<text x="${CARD_W/2}" y="${CARD_H-16}" text-anchor="middle" font-family="'DM Mono',monospace" font-size="7.5"
        fill="${suitColor}" opacity="0.45" letter-spacing="2">${card.element || ''}</text>`
  }
</svg>`;
}

// ── Build card back SVG string ──
export function buildCardBackSVG(w, h) {
  w = w || CARD_W;
  h = h || CARD_H;
  const cx = CARD_W / 2;
  const cy = CARD_H / 2;

  const spokes = Array.from({length: 16}).map((_, i) => {
    const a  = (i / 16) * Math.PI * 2;
    const x1 = (cx + Math.cos(a) * 20).toFixed(1);
    const y1 = (cy + Math.sin(a) * 20).toFixed(1);
    const x2 = (cx + Math.cos(a) * 56).toFixed(1);
    const y2 = (cy + Math.sin(a) * 56).toFixed(1);
    const op = i % 4 === 0 ? 0.3 : 0.12;
    return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#3ecfca" stroke-width="0.4" opacity="${op}"/>`;
  }).join('');

  return `<svg viewBox="0 0 ${CARD_W} ${CARD_H}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" style="display:block;width:100%;height:100%">
  <defs>
    <radialGradient id="backbg" cx="50%" cy="50%">
      <stop offset="0%" stop-color="#0e2830"/>
      <stop offset="100%" stop-color="#050d10"/>
    </radialGradient>
    <radialGradient id="backglow" cx="50%" cy="50%">
      <stop offset="0%" stop-color="rgba(62,207,202,0.2)"/>
      <stop offset="100%" stop-color="rgba(62,207,202,0)"/>
    </radialGradient>
  </defs>

  <!-- Background -->
  <rect width="${CARD_W}" height="${CARD_H}" fill="url(#backbg)"/>

  <!-- Borders -->
  <rect x="6" y="6" width="${CARD_W-12}" height="${CARD_H-12}" rx="2"
    fill="none" stroke="#3ecfca" stroke-width="1" opacity="0.35"/>
  <rect x="10" y="10" width="${CARD_W-20}" height="${CARD_H-20}" rx="1"
    fill="none" stroke="#3ecfca" stroke-width="0.4" opacity="0.18"/>

  <!-- Glow halo -->
  <circle cx="${cx}" cy="${cy}" r="60" fill="url(#backglow)"/>

  <!-- Concentric rings -->
  <circle cx="${cx}" cy="${cy}" r="58" fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.28"/>
  <circle cx="${cx}" cy="${cy}" r="48" fill="none" stroke="#3ecfca" stroke-width="0.6" opacity="0.22"/>
  <circle cx="${cx}" cy="${cy}" r="38" fill="none" stroke="#3ecfca" stroke-width="0.5" opacity="0.18"/>
  <circle cx="${cx}" cy="${cy}" r="28" fill="none" stroke="#3ecfca" stroke-width="0.5" opacity="0.18"/>
  <circle cx="${cx}" cy="${cy}" r="18" fill="rgba(62,207,202,0.06)"/>
  <circle cx="${cx}" cy="${cy}" r="18" fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.4"/>

  <!-- Radial spokes -->
  ${spokes}

  <!-- Centre star -->
  <polygon points="${cx},${cy-10} ${cx+3},${cy-3} ${cx+10},${cy} ${cx+3},${cy+3} ${cx},${cy+10} ${cx-3},${cy+3} ${cx-10},${cy} ${cx-3},${cy-3}"
    fill="none" stroke="#3ecfca" stroke-width="0.8" opacity="0.5"/>
  <circle cx="${cx}" cy="${cy}" r="3" fill="#3ecfca" opacity="0.6"/>

  <!-- Brand -->
  <text x="${cx}" y="${CARD_H-28}" text-anchor="middle"
    font-family="'DM Mono',monospace" font-size="8"
    fill="#3ecfca" opacity="0.3" letter-spacing="3">ECLIPTICA</text>
  <text x="${cx}" y="${CARD_H-20}" text-anchor="middle"
    font-family="'DM Mono',monospace" font-size="6.5"
    fill="#3ecfca" opacity="0.18" letter-spacing="2">CELESTIAL INTELLIGENCE</text>
</svg>`;
}
